import { useEffect, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { Form } from 'factorial-form'
import { PostsGroupHeaderDocument, UpdatePostsGroupDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import GroupForm from 'modules/dashboard/components/GroupForm'

interface Props {
  onClose: () => void
  groupId: number
}

function EditGroup({ onClose, groupId }: Props) {
  const [running, setRunning] = useState(false)

  const { data, loading } = useQuery(PostsGroupHeaderDocument, {
    variables: {
      id: groupId,
    },
  })

  const group = data?.posts.group

  const form = useRef(
    new Form(
      {
        title: group?.title ?? '',
        description: group?.description ?? '',
      },
      {
        title: 'string',
        description: 'string',
      }
    )
  ).current

  useEffect(() => {
    if (group) {
      form.setValues({
        title: group.title ?? '',
        description: group.description ?? '',
      })
    }
  }, [group, form])

  useEffect(() => {
    if (!loading && !group) {
      onClose()
    }
  }, [loading, group, onClose])

  const [updateGroup] = useMutation(UpdatePostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.updatePostsGroup,
  })

  const handleSubmit = async () => {
    if (!group) return

    setRunning(true)

    const result = await updateGroup({
      variables: {
        id: groupId,
        title: form.get('title').value,
        description: form.get('description').value,
      },
      refetchQueries: [
        {
          query: PostsGroupHeaderDocument,
          variables: { id: groupId },
        },
      ],
    })

    const handled = handleResult(result, (_errors) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.error_message'),
      })
    })

    setRunning(false)

    if (handled.success) {
      onClose()
    }
  }

  const disabled = !form.get('title').value || !form.get('description').value

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('dashboard.groups.edit.title')}
      width="sm"
      description={i18n.t('dashboard.groups.edit.description')}
      primaryAction={{
        label: i18n.t('dashboard.groups.edit.title'),
        onClick: handleSubmit,
        disabled: disabled || running,
        loading: running,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
        disabled: running,
      }}
      loading={loading}
    >
      <Box paddingRight="s16">
        <GroupForm form={form} />
      </Box>
    </F0Dialog>
  )
}

export default observer(EditGroup)
